import { Add, ChevronRight, Search } from '@mui/icons-material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Button, InputAdornment, TextField } from '@mui/material';
import { twMerge } from 'tailwind-merge';
import { useDispatch, useSelector } from 'react-redux';

import Loader from '@/components/ui/Loader/Loader';
import BDHelmet from '@/components/BDHelmet';
import GroupAndCategory from '@/components/Modal/Project/GroupAndCategory';
// import { DataTable } from './Table/dataTable';
import JDataTable from './Table/JDataTable';
import project from '@/api/project';
import JFilteredDataTable from './Table/JFilteredDataTable';

const { getProductGroup, getProductCategory: gPC } = project();

export default function Home() {
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();
	const group = searchParams.get('group') || '';
	const category = searchParams.get('category') || '';
	const id = searchParams.get('id') || '';

	const isHomeReset = useSelector((state) => state.trigger.homeReset);

	const [s, setSearch] = useState('');
	const [search] = useDebounce(s, 500);
	const [groupP, setGroupP] = useState(null);
	const [categoryP, setCategoryP] = useState(null);
	const [isNewCategory, setNewCategory] = useState(false);
	const [showsAll, setShowsAll] = useState(false);

	const isCategory = group && category && id;

	useEffect(() => {
		if (isHomeReset) {
			dispatch({ type: 'trigger/triggerAPI', payload: { key: 'homeReset', value: false } });
			setSearch('');
			setGroupP(null);
			setCategoryP(null);
			setShowsAll(false);
		}
	}, [isHomeReset, dispatch]);

	const {
		data: g,
		isFetching: isFetchingG,
		isFetched: isFetchedG,
		refetch: refetchG,
	} = useQuery({
		queryKey: ['project-group'],
		queryFn: () => getProductGroup({ max_size: true, order_by: 'index' }),
	});

	const {
		data: c,
		isFetching: isFetchingC,
		isFetched: isFetchedC,
		refetch: refetchC,
	} = useQuery({
		queryKey: ['project-category', groupP],
		enabled: !!groupP,
		queryFn: () => getProductGroup({}, groupP?.id),
	});

	const {
		data: p,
		isFetching: isFetchingP,
		isFetched: isFetchedP,
	} = useQuery({
		queryKey: ['projects', groupP, categoryP],
		enabled: !!groupP && !!categoryP,
		queryFn: () => gPC({}, categoryP.category_detail.id),
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const groups = useMemo(() => g?.data?.results || g?.data, [g]) || [];
	const categories = useMemo(() => c?.data || null, [c]) || null;
	const projects =
		useMemo(() => {
			if (!search) {
				return !categoryP ? [] : p?.data?.component_relations;
			} else {
				return [];
			}
		}, [search, categoryP, p]) || [];

	const onSaveCategory = () => {
		if (isNewCategory) {
			const { type } = isNewCategory;
			if (type === 'group') {
				refetchG();
			}
			if (type === 'category') {
				refetchC();
			}
			setNewCategory(false);
		}
	};

	const isNotShowingTable = !search && !showsAll;
	const showProject = projects || null
	return (
		<>
			<BDHelmet title='Home' description='BDS Samferdsel home' />
			<div id='home' className={twMerge('container flex flex-col gap-4 pt-8 pb-8 mx-auto', isNotShowingTable ? 'overflow-y-hidden' : '')}>
				<div className='flex gap-2'>
					<TextField
						id='search'
						className='w-full input-search'
						placeholder='Søk'
						value={s}
						onChange={(e) => {
							setSearch(e.target.value);
						}}
						size='small'
						InputProps={{
							startAdornment: (
								<InputAdornment position='start'>
									<Search style={{ color: 'black' }} />
								</InputAdornment>
							),
						}}
					/>
					<Button
						className='!capitalize btn !min-w-24 hover:!bg-bd-blue flex items-center justify-center text-center'
						sx={{
							lineHeight: 'normal',
							height: 'auto',
						}}
						classes={{ endIcon: '!ml-auto' }}
						endIcon={<ChevronRight />}
						onClick={() => setShowsAll(true)}
					>
						Vis alt
					</Button>
				</div>
				<JDataTable isCategory={isCategory} group={group} category={category} id={id} search={search} showsAll={showsAll} />
				{isNotShowingTable && (
					<>
						<div className='grid grid-cols-12 gap-4' style={{ maxHeight: 'calc(100vh - 200px)' }}>
							<div className='scrollable-container flex flex-col w-full col-span-3 gap-4 pr-2 overflow-x-hidden overflow-y-auto max-h-[70vh]'>
								<BoxGroups
									btn={{ label: 'Vis alt innen Samferdsel' }}
									loading={isFetchingG && !isFetchedG}
									noData={isFetchedG && !groups}
									selected={groupP}
									setSelected={(e) => {
										const isSame = e.id === groupP?.id;
										setGroupP(!isSame ? e : null);
										if (groupP && groupP.id !== e?.id) {
											setCategoryP(null);
										}
									}}
									data={groups}
									prefix={['groupname']}
									link={{ groupP, categoryP }}
								/>
								{!isFetchingG && isFetchedG && (
									<button onClick={() => setNewCategory({ type: 'group' })} className={twMerge('box-category bg-bd-gLight')}>
										<span className='text-2xl font-medium text-left text-bd-blue'>Legg til</span>
										<div className={twMerge('icon !bg-bd-gLight')}>
											<Add className='text-bd-blue' />
										</div>
									</button>
								)}
							</div>

							{groupP && (
								<div className='scrollable-container flex flex-col w-full col-span-3 gap-4 pr-2 overflow-x-hidden overflow-y-auto max-h-[70vh]'>
									<BoxGroups
										btn={{ label: `Vis alt innen ${groupP.groupname}` }}
										loading={isFetchingC && !isFetchedC}
										noData={isFetchedC && !categories}
										selected={categoryP}
										setSelected={setCategoryP}
										data={categories?.category_relations?.filter((c) => !!c.category_detail) || [] || []}
										prefix={['category_detail', 'categoryname']}
										link={{ groupP, categoryP }}
									/>
									{!isFetchingC && isFetchedC && (
										<button onClick={() => setNewCategory({ type: 'category' })} className={twMerge('box-category bg-bd-gLight')}>
											<span className='text-2xl font-medium text-left text-bd-blue'>Legg til</span>
											<div className={twMerge('icon !bg-bd-gLight')}>
												<Add className='text-bd-blue' />
											</div>
										</button>
									)}
								</div>
							)}
							<div className='relative flex flex-col col-span-6 gap-4 pt-0'>
								{isFetchingP && !isFetchedP && <Loader />}
								{isFetchedP && !projects.length && (
									<>
										{/* <span>No data</span> */}
										{/* <LinkToAll group={groupP} category={categoryP} /> */}
									</>
								)}
								{isFetchedP && showProject &&
									<JFilteredDataTable data={showProject} />
								}
								{/* {isFetchedP &&
									projects?.slice(0, 10).map((p) => {
										const d = p.product_detail;
										if (!d) return null;
										const isVersion = !!d?.productversion && d?.productversion !== '-';
										return (
											<Link
												to={`${route.project}/${d.productcodenumber}${isVersion ? `_${d.productversion}` : ''}/${d.productid}`}
												className='box-category text-ellipsis'
												key={p.id}
											>
												{d.productprefix} {d.productcodenumber} {d.productname}
											</Link>
										);
									})} */}
								{/* {projects?.length === 10 && <LinkToAll group={groupP} category={categoryP} />} */}
							</div>
						</div>
					</>
				)}
			</div>
			{!!isNewCategory && (
				<GroupAndCategory
					open={!!isNewCategory}
					type={isNewCategory.type}
					onSave={onSaveCategory}
					data={groupP}
					onClose={() => setNewCategory(false)}
					groups={groups}
					refetch={(type) => {
						if (type === 'group') {
							refetchG();
						}
					}}
				/>
			)}
		</>
	);
}

function BoxGroups(props) {
	const { loading, data, selected, setSelected, prefix = null } = props;

	return (
		<div className='scrollable-container flex flex-col col-span-4 gap-4 pr-2 overflow-x-hidden overflow-y-auto max-h-[70vh]'>
			<div className='flex flex-col gap-4'>
				{data?.map((g) => {
					const s = selected?.id === g.id;
					let name = g;
					prefix?.forEach((p) => {
						name = name[p];
					});
					return (
						<button
							onClick={() => {
								setSelected(g);
							}}
							key={g.id}
							className={twMerge('box-category', s ? 'bg-bd-blue text-white' : '')}
						>
							<span className='text-2xl font-medium text-left'>{name}</span>
							<div className={twMerge('icon', s ? '!bg-bd-blue' : '')}>
								<ChevronRight />
							</div>
						</button>
					);
				})}
			</div>
			{loading && <Loader />}
		</div>
	);
}
